// @ts-check
import AppConfig from '../appConfig';
import { SELFSTORAGE_POINT_TYPE } from '../enums/SelfStoragePointType';

/**
 * Every insight-panel URL the map builds, in one place: the in-panel report view,
 * the full-screen "Analyze deal" view and the edit-market view.
 */

export const normalizeReportFromPath = pathname =>
    pathname
        .replace(/^\/selfstorage\//, '')
        .replace(/\/frame$/, '')
        .replace(/\/$/, '');

const defaultReportForPointType = pointType =>
    pointType === SELFSTORAGE_POINT_TYPE.SS_FACILITY
        ? 'selfstorage-point-info'
        : AppConfig.constants.defaultInsightsReport;

/**
 * The report a panel URL must carry. The path the child last reported wins, so a
 * report switch inside the panel survives every src rebuild; only then do the
 * help tour, the last known report and the point-type default apply.
 */
export function resolveInsightsReport({ visualReport, currentInsightReport, helpTourReport }) {
    if (currentInsightReport) {
        return normalizeReportFromPath(currentInsightReport);
    }
    return (
        helpTourReport || visualReport.report || defaultReportForPointType(visualReport.pointType)
    );
}

const selectionParam = visualReport =>
    Array.isArray(visualReport.selection) ? visualReport.selection.join(',') : '';

const query = visualReport =>
    `pois=${visualReport.point.lng};${visualReport.point.lat}` +
    `&profile=${visualReport.profile}` +
    `&selection=${selectionParam(visualReport)}` +
    `&title=${encodeURIComponent(visualReport.title)}`;

const extraParams = (visualReport, elementToFocus) => {
    let extras = '';
    if (visualReport.facilityId) extras += `&facility-id=${visualReport.facilityId}`;
    if (visualReport.pointType) extras += `&point-type=${visualReport.pointType}`;
    if (visualReport.id) extras += `&deal-id=${visualReport.id}`;
    if (elementToFocus) extras += `&element-to-focus=${elementToFocus}`;
    return extras;
};

// A deal keeps its drawn ring after switching to a radius or drive time, so the
// ring is sent only while the shape is the active profile.
const shapeParam = visualReport =>
    visualReport.profile === 'polygon' && visualReport.polygon
        ? `&polygon=${visualReport.polygon}`
        : '';

/**
 * @param {object} options
 * @param {import('../types').VisualReport} options.visualReport
 * @param {string} [options.report] resolved report id, for the report views
 * @param {boolean} [options.frame] in-panel view rather than full screen
 * @param {boolean} [options.edit] edit-market view
 * @param {string} [options.elementToFocus] edit view only
 * @param {number} [options.listId] edit view only
 */
export function buildInsightsUrl({
    visualReport,
    report,
    frame = false,
    edit = false,
    elementToFocus,
    listId,
}) {
    if (!visualReport) return '';
    const base = `${AppConfig.constants.links.dashboardLink}/insights`;

    if (edit) {
        return (
            `${base}/frame/edit?${query(visualReport)}` +
            extraParams(visualReport, elementToFocus) +
            shapeParam(visualReport) +
            (listId ? `&list-id=${listId}` : '')
        );
    }

    return (
        `${frame ? `${base}/frame` : base}?report=${report}&${query(visualReport)}` +
        extraParams(visualReport) +
        shapeParam(visualReport) +
        (frame ? '' : `&return=${encodeURIComponent(window.location.href)}&expanded=1`)
    );
}
